// Minimal HTML sanitizer for product descriptions.
// Goal: allow basic formatting while preventing script injection.
// NOTE: For richer HTML, consider adding DOMPurify. For now we keep it dependency-free.

const VOID_TAGS = new Set(['br']);

const ALLOWED_TAGS = new Set([
  'b',
  'strong',
  'i',
  'em',
  'u',
  'br',
  'p',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
  'h4',
  'span',
  'div',
  'a',
]);

const ALLOWED_ATTRS = {
  a: new Set(['href', 'title', 'target', 'rel']),
  '*': new Set(['class']),
};

const hasDom = () =>
  typeof globalThis !== 'undefined' &&
  typeof globalThis.document !== 'undefined' &&
  typeof globalThis.document.createElement === 'function';

const sanitizeUrl = (href) => {
  if (!href) return '';
  const trimmed = String(href).trim();

  // Disallow dangerous protocols.
  if (/^\s*javascript:/i.test(trimmed)) return '';
  if (/^\s*data:/i.test(trimmed)) return '';

  return trimmed;
};

export const sanitizeHtml = (unsafeHtml) => {
  if (unsafeHtml === null || unsafeHtml === undefined) return '';
  const input = String(unsafeHtml);
  if (!input) return '';
  if (!hasDom()) return input;

  // Parse into a detached DOM.
  const template = globalThis.document.createElement('template');
  template.innerHTML = input;

  const escapeText = (text) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const getAllowedAttrsForTag = (tag) =>
    new Set([
      ...(ALLOWED_ATTRS['*'] ? Array.from(ALLOWED_ATTRS['*']) : []),
      ...(ALLOWED_ATTRS[tag] ? Array.from(ALLOWED_ATTRS[tag]) : []),
    ]);

  const buildAttributes = (el, tag) => {
    const allowedAttrs = getAllowedAttrsForTag(tag);
    const out = [];

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value;

      // Block inline handlers always.
      if (name.startsWith('on')) continue;

      if (!allowedAttrs.has(name)) continue;

      if (tag === 'a' && name === 'href') {
        const safe = sanitizeUrl(value);
        if (!safe) continue;
        out.push(`href="${safe.replace(/"/g, '&quot;')}"`);
        continue;
      }

      out.push(`${name}="${String(value).replace(/"/g, '&quot;')}"`);
    }

    // Hardening: rel noopener/noreferrer for target=_blank.
    if (tag === 'a') {
      const target = (el.getAttribute('target') || '').toLowerCase();
      if (target === '_blank') {
        const hasRel = out.some((a) => a.startsWith('rel='));
        if (!hasRel) out.push('rel="noopener noreferrer"');
      }
    }

    return out.length ? ` ${out.join(' ')}` : '';
  };

  const serialize = (node) => {
    if (node.nodeType === globalThis.Node.COMMENT_NODE) return '';
    if (node.nodeType === globalThis.Node.TEXT_NODE)
      return escapeText(node.textContent || '');

    if (node.nodeType !== globalThis.Node.ELEMENT_NODE) {
      // Document fragments etc.
      return Array.from(node.childNodes || [])
        .map(serialize)
        .join('');
    }

    const tag = node.tagName.toLowerCase();

    // Remove unsafe tags entirely but keep *content*.
    // (e.g., <script>alert(1)</script> -> "alert(1)")
    if (!ALLOWED_TAGS.has(tag)) {
      return Array.from(node.childNodes || [])
        .map(serialize)
        .join('');
    }

    const attrs = buildAttributes(node, tag);
    if (VOID_TAGS.has(tag)) return `<${tag}${attrs}>`;

    const children = Array.from(node.childNodes || [])
      .map(serialize)
      .join('');
    return `<${tag}${attrs}>${children}</${tag}>`;
  };

  return Array.from(template.content.childNodes).map(serialize).join('');
};
